import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  Wallet,
  FileText,
  Menu,
  X,
  LogOut,
  User,
  Building2,
  ChevronRight,
  Shield,
  IndianRupee,
  Settings,
  Database,
  BookOpen,
  Plus,
  UserPlus,
  CalendarPlus,
  Receipt,
  Zap,
  Bell,
  Newspaper,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ForcePasswordChange from './ForcePasswordChange';
import { notifications } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { hasModuleAccess } from '../lib/modules';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [notificationList, setNotificationList] = useState([]);
  const { user, logout, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };
  
  // Show password change modal if force_password_change is true
  const showPasswordChangeModal = user?.force_password_change === true;

  // Quick Actions based on user role
  const quickActions = [
    { name: 'User Manual', icon: BookOpen, href: '/user-manual', publicToAuthenticated: true },
    { name: 'Leave Management', icon: Calendar, href: '/leaves', roles: ['Admin', 'Manager', 'Employee'], moduleId: 'leaves', permission: 'view' },
    { name: 'Add Employee', icon: UserPlus, href: '/employees', action: 'add-employee', roles: ['Admin'], moduleId: 'employees', permission: 'add' },
    { name: 'Apply Leave', icon: CalendarPlus, href: '/leaves', action: 'apply-leave', roles: ['Admin', 'Manager', 'Employee'], moduleId: 'leaves', permission: 'add' },
    { name: 'View Payslip', icon: Receipt, href: '/payslips', action: 'view-payslip', publicToAuthenticated: true, moduleId: 'payslips', permission: 'view' },
  ];

  const filteredQuickActions = quickActions.filter(item => (
    (item.publicToAuthenticated || item.roles?.includes(user?.role)) && hasModuleAccess(user, item.moduleId, item.permission)
  ));

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, publicToAuthenticated: true, moduleId: 'dashboard' },
    { name: 'Feeds', href: '/feeds', icon: Newspaper, roles: ['Admin', 'Manager', 'Employee'], moduleId: 'feeds' },
    { name: 'Employees', href: '/employees', icon: Users, roles: ['Admin', 'Manager', 'HR'], moduleId: 'employees' },
    { name: 'User Management', href: '/users', icon: Shield, roles: ['Admin'], moduleId: 'users' },
    { name: 'Masters', href: '/masters', icon: Database, roles: ['Admin'], moduleId: 'masters' },
    { name: 'Attendance', href: '/attendance', icon: Clock, roles: ['Admin', 'Manager', 'Employee'], moduleId: 'attendance' },
    { name: 'Leave Management', href: '/leaves', icon: Calendar, roles: ['Admin', 'Manager', 'Employee'], moduleId: 'leaves' },
    { name: 'Holidays', href: '/holidays', icon: Calendar, roles: ['Admin', 'Manager', 'Employee'], moduleId: 'holidays' },
    { name: 'Salary Components', href: '/salary-components', icon: IndianRupee, publicToAuthenticated: true, moduleId: 'salary-structure' },
    { name: 'Salary Structure', href: '/salary-structure', icon: IndianRupee, publicToAuthenticated: true, moduleId: 'salary-structure' },
    { name: 'Payroll Inputs', href: '/payroll-inputs', icon: Wallet, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Payroll Processing', href: '/payroll-processing', icon: Wallet, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Payroll Approvals', href: '/payroll-approvals', icon: Shield, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Statutory Configuration', href: '/statutory-configuration', icon: Shield, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Full & Final', href: '/full-final-settlements', icon: FileText, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Payroll', href: '/payroll', icon: Wallet, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Payroll Reports', href: '/payroll-reports', icon: FileText, publicToAuthenticated: true, moduleId: 'payroll-reports' },
    { name: 'Payslips', href: '/payslips', icon: Receipt, publicToAuthenticated: true, moduleId: 'payslips' },
    { name: 'Payments & Accounting', href: '/payments-accounting', icon: Wallet, publicToAuthenticated: true, moduleId: 'payroll' },
    { name: 'Reports', href: '/reports', icon: FileText, roles: ['Admin', 'Manager'], moduleId: 'reports' },
    { name: 'My Documents', href: '/my-documents', icon: FileText, publicToAuthenticated: true },
    { name: 'Info Hub', href: '/info-hub', icon: BookOpen, roles: ['Admin', 'Manager', 'Employee'], moduleId: 'info-hub' },
    { name: 'User Manual', href: '/user-manual', icon: BookOpen, publicToAuthenticated: true },
    { name: 'Admin Settings', href: '/admin/settings', icon: Settings, roles: ['Admin'], moduleId: 'company-settings' },
  ];

  const filteredNav = navigation.filter(item => (
    (item.publicToAuthenticated || item.roles?.includes(user?.role)) && hasModuleAccess(user, item.moduleId)
  ));
  const unreadNotifications = notificationList.filter(item => !item.is_read).length;

  useEffect(() => {
    if (!user?.user_id) return undefined;
    let active = true;
    const loadNotifications = async () => {
      try {
        const res = await notifications.getAll();
        if (active) setNotificationList(res.data || []);
      } catch {
        if (active) setNotificationList([]);
      }
    };
    loadNotifications();
    const timer = setInterval(loadNotifications, 60000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [user?.user_id]);

  const handleNotificationClick = async (notification) => {
    try {
      await notifications.markRead(notification.notification_id);
      setNotificationList(prev => prev.map(item => (
        item.notification_id === notification.notification_id ? { ...item, is_read: true } : item
      )));
    } catch {
      // Keep navigation responsive even if marking read fails.
    }
    const ref = notification.reference || {};
    if (notification.type?.includes('leave')) {
      const type = notification.type || '';
      const canOpenTeamLeave = type === 'leave_request' && ['Admin', 'Manager'].includes(user?.role);
      const canOpenOwnLeave = ['leave_approved', 'leave_rejected'].includes(type) &&
        (!ref.employee_id || ref.employee_id === user?.employee_id);
      const shouldHighlightLeave = Boolean(ref.leave_id && (canOpenTeamLeave || canOpenOwnLeave));

      navigate('/leaves', {
        state: {
          view: canOpenTeamLeave ? 'team' : 'self',
          ...(shouldHighlightLeave ? { highlightLeaveId: ref.leave_id, leaveId: ref.leave_id } : {})
        }
      });
    }
    else if (notification.type?.includes('compoff')) navigate('/attendance', { state: { action: 'view-compoff' } });
    else if (ref.employee_id) navigate('/employees');
  };
  const currentPageTitle = location.pathname === '/profile'
    ? 'Profile'
    : filteredNav.find(item => location.pathname.startsWith(item.href))?.name || 'Dashboard';

  const getInitials = (email) => {
    if (!email) return 'U';
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar Backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 bg-white border-r border-border
          transform transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:relative lg:inset-auto lg:flex-shrink-0
          ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}
          w-64
        `}
      >
          <div className="h-full flex flex-col">
            {/* Logo & Header Toggle */}
            <div className={`h-16 flex items-center border-b border-border px-4 ${sidebarCollapsed ? 'lg:justify-center' : 'justify-between'}`}>
              <div className="flex items-center gap-2 overflow-hidden">
                {!sidebarCollapsed ? (
                  <img src="/ivhr-logo.png" alt="IVHR" className="h-11 w-36 object-contain object-left" />
                ) : (
                  <img src="/ivhr-logo.png" alt="IVHR" className="h-9 w-28 object-contain object-center lg:h-9 lg:w-9 lg:object-cover lg:object-left" />
                )}
              </div>

              {/* Desktop Sidebar Toggle Button (Inside Sidebar Header) */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:inline-flex shrink-0 hover:bg-secondary text-muted-foreground hover:text-foreground"
                onClick={toggleSidebar}
                title={sidebarCollapsed ? "Expand Menu" : "Collapse Menu"}
                data-testid="toggle-sidebar-button"
              >
                <Menu className="h-5 w-5" />
              </Button>

              {/* Mobile Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
                data-testid="close-sidebar-button"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
              {filteredNav.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    title={sidebarCollapsed ? item.name : undefined}
                    className={`
                      flex items-center gap-3 py-3 rounded-lg transition-all
                      ${sidebarCollapsed ? 'lg:justify-center lg:px-0 px-4' : 'px-4'}
                      ${isActive 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                      }
                    `}
                  >
                    <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                    <span className={`font-medium text-sm truncate ${sidebarCollapsed ? 'lg:hidden' : 'block'}`}>{item.name}</span>
                    {isActive && <ChevronRight className={`h-4 w-4 ml-auto shrink-0 ${sidebarCollapsed ? 'lg:hidden' : 'block'}`} />}
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="p-3 border-t border-border">
              <div className={`flex items-center gap-3 py-2 ${sidebarCollapsed ? 'lg:justify-center lg:px-0 px-3' : 'px-3'}`} title={sidebarCollapsed ? user?.email : undefined}>
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                    {getInitials(user?.email)}
                  </AvatarFallback>
                </Avatar>
                <div className={`flex-1 min-w-0 ${sidebarCollapsed ? 'lg:hidden' : 'block'}`}>
                  <p className="text-sm font-medium text-foreground truncate">{user?.email}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="min-h-16 bg-white border-b border-border sticky top-0 z-30 glass-effect">
          <div className="min-h-16 px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-4">
              {/* Mobile Sidebar Open Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden shrink-0"
                onClick={() => setSidebarOpen(true)}
                data-testid="open-sidebar-button"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className={location.pathname === '/dashboard' ? 'min-w-0' : 'hidden lg:block'}>
                <h2 className="text-xl font-semibold text-foreground tracking-tight">
                  {currentPageTitle}
                </h2>
                {location.pathname === '/dashboard' && <p className="mt-1 text-xs sm:text-sm text-muted-foreground break-words">
                  Welcome, {user?.name || user?.email}{user?.company_name ? ` · ${user.company_name}` : ''}
                </p>}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex shrink-0 items-center gap-2">
              {/* Quick Actions Dropdown */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input shadow-sm hover:bg-accent hover:text-accent-foreground h-8 px-3 text-xs" data-testid="quick-actions-dropdown">
                  <Zap className="h-4 w-4" />
                  <span className="hidden sm:inline">Quick Actions</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Quick Actions
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {filteredQuickActions.map((action) => (
                    <DropdownMenuItem 
                      key={action.name}
                      onClick={() => navigate(action.href, action.action ? { state: { action: action.action } } : undefined)}
                      className="cursor-pointer"
                      data-testid={`quick-action-${action.action || action.moduleId}`}
                    >
                      <action.icon className="mr-2 h-4 w-4" />
                      {action.name}
                    </DropdownMenuItem>
                  ))}
                  {filteredQuickActions.length === 0 && (
                    <DropdownMenuItem disabled>No quick actions available</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="relative inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring hover:bg-accent hover:text-accent-foreground h-9 w-9" data-testid="notification-menu-button">
                  <Bell className="h-5 w-5" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -right-1 -top-1 min-w-5 h-5 rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-5 text-white">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Notifications ({notificationList.length})</span>
                    {unreadNotifications > 0 && <span className="text-xs text-red-600">{unreadNotifications} unread</span>}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notificationList.length === 0 ? (
                    <DropdownMenuItem disabled>No notifications</DropdownMenuItem>
                  ) : (
                    notificationList.slice(0, 8).map(notification => (
                      <DropdownMenuItem
                        key={notification.notification_id}
                        className="cursor-pointer items-start"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="space-y-1">
                          <p className={`text-sm ${notification.is_read ? 'font-normal' : 'font-semibold'}`}>{notification.title}</p>
                          <p className="line-clamp-2 text-xs text-muted-foreground">{notification.message}</p>
                          <p className="text-[11px] text-muted-foreground">{formatDateTime(notification.created_at)}</p>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-9 w-9" data-testid="user-menu-button">
                  <Avatar className="h-9 w-9 pointer-events-none">
                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                      {getInitials(user?.email)}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.email}</p>
                      <p className="text-xs text-muted-foreground">{user?.role}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer"
                    data-testid="profile-menu-item"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout().catch(() => window.alert('Logout failed. Please retry.'))} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 min-w-0 overflow-x-hidden p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Force Password Change Modal */}
      <ForcePasswordChange 
        isOpen={showPasswordChangeModal} 
        onSuccess={() => {
          // Modal will close automatically after updateUser is called
        }} 
      />
    </div>
  );
};

export default Layout;
